import {isUndefined, last} from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {AsyncHandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {buildCustomViewPath} from '../../../../helpers/models/customViewHelpers';
import {VisualRecipientMap} from '../../../../helpers/models/recipientHelpers';
import {wrapActionCreator} from '../../../../helpers/reduxHelpers';
import {AppParametricSelector, makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {MessageModel, MessageStatusesEnum} from '../../../../models/api/messageModel';
import {RecipientRolesEnum} from '../../../../models/api/recipientModel';
import {TagModel} from '../../../../models/api/tagModel';
import {VisualContactModel} from '../../../../models/api/visualContactModel';
import {VisualRecipientModel} from '../../../../models/api/visualRecipientModel';
import {getCellsParameters} from '../../../../services/legacy/legacySelectors';
import {AppState} from '../../../../store/appState';
import {Dispatch} from '../../../../store/appStore';
import {
  makeGetVisualRecipient,
  makeGetVisualRecipientMap,
} from '../../../../store/data/contacts/recipientsSelectors';
import {addAndRemoveTagsFromConversationsAction} from '../../../../store/data/conversations/tagConversationsActions';
import {getConversations} from '../../../../store/data/dataSelectors';
import {toggleHideTagAction} from '../../../../store/data/preferences/preferencesActions';
import {createQuickRuleAction} from '../../../../store/data/rules/rulesActions';
import {getUser, getUserId, getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {showWorkspaceTagAsyncAction} from '../../../../store/data/workspace/workspaceActions';
import {pushPathAndFocusAction} from '../../../../store/panels/app/appPanelRouteActions';
import {InboxesParametersTypesEnum} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {getPanel} from '../../../../store/panels/panelsSelectors';
import {NewPanelId, PanelTypesEnum} from '../../../../store/panels/panelStateBase';
import {ViewerPanelPageTypesEnum} from '../../../../store/panels/viewer/viewerPanelState';
import {
  makeGetViewerMessages,
  OptionalViewerPanelPageSelector,
} from '../../../inboxes/side/sideFrameSelectors';
import {CreateTagRuleDropdown, RuleTemplate} from './createTagRuleDropdown';

/*
 * Props.
 */

interface CreateTagRuleDropdownContainerProps {
  conversationIds?: ReadonlyArray<number>;
  message?: MessageModel;
  panelId?: NewPanelId;
  contact?: VisualContactModel;
  tag?: TagModel;
}

/*
 * Selectors.
 */

function getConversationId(_state: AppState, props: CreateTagRuleDropdownContainerProps) {
  return props.conversationIds && props.conversationIds[0];
}

function getTag(_state: AppState, props: CreateTagRuleDropdownContainerProps) {
  return props.tag;
}

function getMessage(_state: AppState, props: CreateTagRuleDropdownContainerProps) {
  return props.message;
}

function getCurrentPanel(state: AppState, props: CreateTagRuleDropdownContainerProps) {
  if (!props.panelId) {
    return null;
  }

  return getPanel(state, {panelId: props.panelId});
}

function makeGetConversation() {
  return createSelector([getConversations, getConversationId], (conversations, conversationId) =>
    isUndefined(conversationId) ? undefined : conversations[conversationId],
  );
}

function makeGetCurrentPage() {
  return createSelector(getCurrentPanel, (currentPanel) => {
    if (!currentPanel || currentPanel.type !== PanelTypesEnum.VIEWER) {
      return undefined;
    }

    const currentPage = currentPanel.pages.current;
    if (currentPage?.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION) {
      return undefined;
    }

    return currentPage;
  });
}

function makeGetTeammateId<P>(getCurrentPage: OptionalViewerPanelPageSelector<P>) {
  return createSelector(
    [getUserId, getCurrentPage],
    (userId, currentPage) => (currentPage && currentPage.cellsContext.teammateId) || userId,
  );
}

function makeGetLastIncomingVisualRecipientFromConversation<P>(
  getVisualRecipient: AppParametricSelector<P, VisualRecipientModel | undefined>,
) {
  return createSelector([getVisualRecipient], (visualRecipient) =>
    visualRecipient && visualRecipient.role === RecipientRolesEnum.FROM ? [visualRecipient] : [],
  );
}

function makeGetLastInboundMessage<P>(getMessages: AppParametricSelector<P, ReadonlyArray<MessageModel>>) {
  return createSelector([getMessages, getMessage], (messages, message) => {
    if (message) {
      return message;
    }

    if (!messages) {
      return undefined;
    }

    const inboundMessages = messages.filter((m) => m.status === MessageStatusesEnum.IMPORTED);
    return last(inboundMessages);
  });
}

function makeGetLastIncomingVisualRecipient<P>(
  getVisualRecipientMap: AppParametricSelector<P, VisualRecipientMap>,
) {
  return createSelector([getVisualRecipientMap], (visualRecipientMap) => {
    if (!visualRecipientMap) {
      return [];
    }

    return visualRecipientMap.from || [];
  });
}

function makeGetUserInboxTagId() {
  return createSelector([getUser], (user) => user.inboxTagId);
}

function makeGetCurrentCellsFilters() {
  return createSelector([getCellsParameters], (parameters) => {
    if (!parameters || parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
      return undefined;
    }

    return parameters.filters;
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CreateTagRuleDropdownContainerProps>()(() => {
  const getConversation = makeGetConversation();
  const getCurrentPage = makeGetCurrentPage();
  const getMessages = makeGetViewerMessages(getCurrentPage);
  const getLastInboundMessage = makeGetLastInboundMessage(getMessages);
  const getVisualRecipient = makeGetVisualRecipient(
    getConversation,
    makeGetTeammateId(getCurrentPage),
    'createTagRuleDropdownContainer',
  );
  const getUserInboxTagId = makeGetUserInboxTagId();
  const getVisualRecipientMap = makeGetVisualRecipientMap(getUserNamespace, getLastInboundMessage);
  return {
    conversationRecipients: makeGetLastIncomingVisualRecipientFromConversation(getVisualRecipient),
    messageRecipients: makeGetLastIncomingVisualRecipient(getVisualRecipientMap),
    tag: getTag,
    userNamespace: getUserNamespace,
    inboxTagId: getUserInboxTagId,
    currentFilters: makeGetCurrentCellsFilters(),
  };
});

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    createQuickRule: wrapActionCreator(dispatch, createQuickRuleAction),
    addAndRemoveTagsFromConversations: wrapActionCreator(dispatch, addAndRemoveTagsFromConversationsAction),
    toggleHideTag: wrapActionCreator(dispatch, toggleHideTagAction),
    showWorkspaceTag: showWorkspaceTagAsyncAction,
    pushPathAndFocus: wrapActionCreator(dispatch, pushPathAndFocusAction),
  };
}

export const CreateTagRuleDropdownContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const createRule: AsyncHandlerOf<RuleTemplate> = async (template) => {
      dispatchProps.createQuickRule(template);
      dispatchProps.showWorkspaceTag(template.tag.id, template.tag.isView, template.tag.namespace);
      const shouldTagBeVisibleInWorkspace = true;
      dispatchProps.toggleHideTag(template.tag.id, [template.tag], {
        value: shouldTagBeVisibleInWorkspace,
      });
      const conversationIds =
        (ownProps.message && [ownProps.message.conversationId]) || ownProps.conversationIds;
      if (!conversationIds || conversationIds.length === 0) {
        return;
      }

      dispatchProps.addAndRemoveTagsFromConversations(
        conversationIds,
        {
          add: [template.tag.id],
          remove: [stateProps.inboxTagId],
        },
        true,
      );

      if (!ownProps.panelId) {
        return;
      }

      const tagView = buildCustomViewPath(template.tag.id, {
        filter: stateProps.currentFilters && stateProps.currentFilters[0],
        isTeammateView: true,
      });

      dispatchProps.pushPathAndFocus(ownProps.panelId, `${tagView}/${conversationIds[0]}`);
    };

    const maybeContact = ownProps.contact && {...ownProps.contact, role: RecipientRolesEnum.FROM};
    const recipients =
      (maybeContact && [maybeContact]) ||
      (stateProps.messageRecipients.length && stateProps.messageRecipients) ||
      stateProps.conversationRecipients;

    return {
      ...ownProps,
      ...stateProps,
      recipients,
      createRule,
    };
  },
)(CreateTagRuleDropdown);
